import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { FormControl, FormGroup, FormsModule, Validators } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { PasswordModule } from 'primeng/password';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { Router, ActivatedRoute } from '@angular/router';
import { AuthService } from '../../core/services/auth';
import { UserService } from '../../core/services/user.service';
import { LanguageService } from '../../core/services/language.service';
import { SubscriberService } from '../../core/services/subscriber.service';
import { EncryptionService } from '../../core/services/encryption';
import { SETTINGS } from '../../core/config/common.settings';

@Component({
  selector: 'app-two-factor-auth',
  imports: [
    InputGroupModule, 
    InputGroupAddonModule, 
    FormsModule, 
    InputTextModule, 
    CommonModule, 
    ReactiveFormsModule, 
    ButtonModule, 
    PasswordModule, 
    ToastModule
  ],
  providers: [MessageService],
  templateUrl: './two-factor-auth.html',
  styleUrl: './two-factor-auth.scss'
})
export class TwoFactorAuth implements OnInit {
  @ViewChild('otpInput0') otpInput0!: ElementRef;
  @ViewChild('otpInput1') otpInput1!: ElementRef;
  @ViewChild('otpInput2') otpInput2!: ElementRef;
  @ViewChild('otpInput3') otpInput3!: ElementRef;
  @ViewChild('otpInput4') otpInput4!: ElementRef;
  @ViewChild('otpInput5') otpInput5!: ElementRef;

  value: string = '';
  loading: boolean = false;
  resendLoading: boolean = false;
  email: string = '';
  userId: string = '';
  isLoginVerification: boolean = false;
  otpDigits: string[] = ['', '', '', '', '', ''];
  activeIndex: number = 0;
  otpForm: FormGroup = new FormGroup({
    otp: new FormControl('', [Validators.required, Validators.minLength(6), Validators.maxLength(6)]),
  });

  constructor(
    private authService: AuthService,
    private messageService: MessageService,
    private router: Router,
    private route: ActivatedRoute,
    private userService: UserService,
    private languageService: LanguageService,
    private subscriberService: SubscriberService,
    private encryption: EncryptionService
  ) {}

  ngOnInit() {
    // Load saved language preference and apply it if different from current URL
    this.loadAndApplySavedLanguagePreference();
    
    // Check if this is a login verification flow
    this.userId = sessionStorage.getItem('loginUserId') || '';
    this.email = sessionStorage.getItem('loginEmail') || '';
    
    if (this.userId && this.email) {
      this.isLoginVerification = true;
      this.messageService.add({
        severity: 'info',
        summary: 'Login Verification',
        detail: 'Please enter the verification code sent to your email to complete login.',
        life: 4000
      });
    } else {
      // Fallback to email verification flow (for other use cases)
      this.route.params.subscribe(params => {
        if (params['email']) {
          this.email = params['email'];
          this.sendOTP();
        }
      });
    }
    
    // Set initial focus after view initialization
    setTimeout(() => {
      this.setActiveIndex(0);
    }, 100);
  }

  // Custom OTP input methods
  onOtpInput(event: any, index: number) {
    const input = event.target;
    const value = input.value;
    
    // Only allow numeric input
    if (!/^\d*$/.test(value)) {
      this.otpDigits[index] = '';
      return;
    }
    
    // Ensure only one digit is stored and it's the last character entered
    const lastChar = value.slice(-1);
    this.otpDigits[index] = lastChar;
    this.updateValue();
    
    // Move to next input if value is entered and not at the last input
    if (lastChar && index < 5) {
      this.setActiveIndex(index + 1);
    }
  }

  onOtpKeydown(event: any, index: number) {
    // Handle backspace
    if (event.key === 'Backspace') {
      if (this.otpDigits[index]) {
        // Clear current input
        this.otpDigits[index] = '';
        this.updateValue();
      } else if (index > 0) {
        // Move to previous input and clear it
        this.setActiveIndex(index - 1);
        this.otpDigits[index - 1] = '';
        this.updateValue();
      }
    }
    
    // Handle arrow keys
    if (event.key === 'ArrowLeft' && index > 0) {
      this.setActiveIndex(index - 1);
    }
    if (event.key === 'ArrowRight' && index < 5) {
      this.setActiveIndex(index + 1);
    }
    
    // Handle number keys (0-9)
    if (/^[0-9]$/.test(event.key)) {
      // Prevent default to avoid double input
      event.preventDefault();
      this.otpDigits[index] = event.key;
      this.updateValue();
      
      // Move to next input if not at the last input
      if (index < 5) {
        this.setActiveIndex(index + 1);
      }
    }
    
    // Handle Enter key to submit
    if (event.key === 'Enter') {
      if (this.value.length === 6) {
        this.onVerifyOTP();
      }
    }
  }

  onOtpPaste(event: ClipboardEvent) {
    event.preventDefault();
    const pastedData = event.clipboardData?.getData('text/plain') || '';
    
    // Only process if pasted data contains exactly 6 digits
    if (/^\d{6}$/.test(pastedData)) {
      this.otpDigits = pastedData.split('');
      this.updateValue();
      this.setActiveIndex(5); // Focus last input
    }
  }

  setActiveIndex(index: number) {
    this.activeIndex = index;
    // Focus the input at the specified index using ViewChild references
    setTimeout(() => {
      const inputRefs = [
        this.otpInput0,
        this.otpInput1,
        this.otpInput2,
        this.otpInput3,
        this.otpInput4,
        this.otpInput5
      ];
      
      if (inputRefs[index] && inputRefs[index].nativeElement) {
        inputRefs[index].nativeElement.focus();
      }
    }, 0);
  }

  onOtpClick(index: number) {
    this.setActiveIndex(index);
  }

  updateValue() {
    this.value = this.otpDigits.join('');
  }

  clearOtpInputs() {
    this.otpDigits = ['', '', '', '', '', ''];
    this.value = '';
    this.activeIndex = 0;
    this.setActiveIndex(0);
  }

  sendOTP() {
    if (this.isLoginVerification) {
      // For login verification, OTP is already sent during login
      this.messageService.add({
        severity: 'info',
        summary: 'Info',
        detail: 'Verification code was sent during login. Please check your email.'
      });
    } else {
      // For email verification flow
      this.authService.sendEmailVerificationOTP(this.email).subscribe({
        next: (response) => {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: response.message
          });
        },
        error: (error) => {
          const errorMessage = this.extractErrorMessage(error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: errorMessage
          });
        }
      });
    }
  }

  onVerifyOTP() {
    if (this.value.length === 6) {
      this.loading = true;
      
        if (this.isLoginVerification) {
        // Handle login OTP verification
        this.authService.verifyLoginOTP({ userId: this.userId, otp: this.value }).subscribe({
          next: (response) => {
            this.loading = false;
            
            this.messageService.add({
              severity: 'success',
              summary: 'Success',
              detail: response.message
            });
            console.log(response);
            
            // Clear session storage
            sessionStorage.removeItem('loginUserId');
            sessionStorage.removeItem('loginEmail');
            
            // Load and save language preference (without redirecting)
            // Wait a bit for subscription language to load if needed, then route
            this.loadLanguagePreference(response.user).then(() => {
              // Route user based on setup step and subscription status
              // Language will be read from localStorage for routing
              console.log('response.user', response.user);
              this.routeUserBasedOnSetupStep(response.user);
            });
          },
          error: (error) => {
            this.loading = false;
            const errorMessage = this.extractErrorMessage(error);
            
            this.messageService.add({
              severity: 'error',
              summary: 'Verification Failed',
              detail: errorMessage,
              life: 5000 // Show error for 5 seconds
            });
          }
        });
      } else {
        // Handle email verification flow
        this.authService.verifyEmailOTP(this.email, this.value).subscribe({
          next: (response) => {
            this.loading = false;
            this.messageService.add({
              severity: 'success',
              summary: 'Success',
              detail: response.message
            });
            
            setTimeout(() => {
              this.router.navigate(['/']);
            }, 2000);
          },
          error: (error) => {
            this.loading = false;
            const errorMessage = this.extractErrorMessage(error);
            
            this.messageService.add({
              severity: 'error',
              summary: 'Verification Failed',
              detail: errorMessage,
              life: 5000 // Show error for 5 seconds
            });
          }
        });
      }
    } else {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validation Error',
        detail: 'Please enter a valid 6-digit verification code.'
      });
    }
  }

  onResendOTP() {
    if (this.isLoginVerification) {
      // For login verification, we can't resend OTP as it's part of the login flow
      this.messageService.add({
        severity: 'info',
        summary: 'Info',
        detail: 'Please go back to login and try again to receive a new verification code.'
      });
    } else {
      // For email verification flow
      this.resendLoading = true;
      this.authService.sendEmailVerificationOTP(this.email).subscribe({
        next: (response) => {
          this.resendLoading = false;
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: response.message
          });
        },
        error: (error) => {
          this.resendLoading = false;
          const errorMessage = this.extractErrorMessage(error);
          
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: errorMessage
          });
        }
      });
    }
  }

  onBackToLogin() {
    // Clear session storage when going back to login
    sessionStorage.removeItem('loginUserId');
    sessionStorage.removeItem('loginEmail');
    this.router.navigate(['/']);
  }

  /**
   * Get language from current URL path
   */
  private getLanguageFromUrl(): string | null {
    const path = window.location.pathname;
    const match = path.match(/^\/(en|de|ro)(\/|$)/);
    return match ? match[1] : null;
  }

  /**
   * Load saved language preference and redirect if needed
   */
  private loadAndApplySavedLanguagePreference(): void {
    // Only apply language redirects in production/deployment
    if (!this.languageService.isLanguageFeatureEnabled()) {
      return;
    }

    const savedLanguage = localStorage.getItem('preferredLanguage');
    const currentUrlLang = this.getLanguageFromUrl();
    
    // Only apply if:
    // 1. There's a saved language preference
    // 2. It's a valid language
    // 3. Current URL language doesn't match saved preference
    if (savedLanguage && 
        this.languageService.isValidLanguage(savedLanguage) && 
        currentUrlLang !== savedLanguage) {
      // Redirect to the saved language path
      const currentPath = window.location.pathname;
      const pathWithoutLang = currentPath.replace(/^\/(en|de|ro)(\/|$)/, '/');
      const newPath = `/${savedLanguage}${pathWithoutLang === '/' ? '' : pathWithoutLang}`;
      
      // Only redirect if paths are different
      if (window.location.pathname !== newPath) {
        window.location.href = newPath;
      }
    }
  }

  private maskEmail(email: string): string {
    if (!email) return '';
    
    const atIndex = email.indexOf('@');
    if (atIndex === -1) return email;
    
    const username = email.substring(0, atIndex);
    const domain = email.substring(atIndex);
    
    if (username.length <= 4) {
      return username + '****' + domain;
    } else {
      const visiblePart = username.substring(0, 4);
      const maskedPart = '*'.repeat(username.length - 4);
      return visiblePart + maskedPart + domain;
    }
  }

  get maskedEmail(): string {
    return this.maskEmail(this.email);
  }

  private extractErrorMessage(error: any): string {
    console.log('Raw error object:', error);
    
    // Handle different error formats
    let message = '';
    
    if (error.error?.message) {
      message = error.error.message;
    } else if (error.message) {
      message = error.message;
    } else if (typeof error === 'string') {
      message = error;
    } else {
      message = 'Failed to verify code. Please try again.';
    }
    
    const lowerMessage = message.toLowerCase();
    
    // Handle server down/network errors specifically
    if (error.status === 0 || lowerMessage.includes('0 unknown error')) {
      return 'Unable to connect to the server. Please check your internet connection and try again.';
    }
    
    if (error.status >= 500) {
      return 'Server error. Please try again later.';
    }
    
    // Handle specific OTP-related error messages
    if (lowerMessage.includes('invalid') && lowerMessage.includes('otp')) {
      return 'Invalid verification code. Please check the code and try again.';
    }
    
    if (lowerMessage.includes('expired')) {
      return 'Verification code has expired. Please request a new code.';
    }
    
    if (lowerMessage.includes('not found')) {
      return 'User not found. Please try logging in again.';
    }
    
    // Return the original message if no specific handling is needed
    return message;
  }

  /**
   * Load language preference and save it, but don't redirect
   * Loads user language synchronously, subscription language asynchronously
   * Returns a Promise that resolves when language is loaded
   */
  private loadLanguagePreference(user: any): Promise<void> {
    // Priority: 1. User language preference, 2. Subscription language, 3. Default (en)
    let languageToApply = 'en';
    
    console.log('Loading language preference for user:', {
      userId: user?._id,
      userLanguage: user?.language,
      subscriberId: user?.subscriberId?._id || user?.subscriberId
    });
    
    // Check user language preference first (synchronous)
    if (user?.language && this.languageService.isValidLanguage(user.language)) {
      languageToApply = user.language;
      console.log('Using user language preference:', languageToApply);
      // Save the language preference
      this.languageService.setLanguage(languageToApply);
      localStorage.setItem('preferredLanguage', languageToApply);
      return Promise.resolve(); // User language found, no need to check subscription
    }
    
    console.log('No user language preference found, loading subscription language...');
    
    // Fallback to subscription language - get subscriberId from user object
    const subscriberId = user?.subscriberId?._id || user?.subscriberId;
    
    if (subscriberId) {
      // Load subscription language asynchronously and return promise
      return this.subscriberService.findSubscriber(subscriberId).then((subscriber: any) => {
        const actualSubscriber = subscriber?.subscriber || subscriber;
        if (actualSubscriber?.language && this.languageService.isValidLanguage(actualSubscriber.language)) {
          languageToApply = actualSubscriber.language;
          console.log('Subscription language loaded:', {
            subscriberId: subscriberId,
            language: languageToApply
          });
        } else {
          console.log('No valid subscription language found, using default:', languageToApply);
        }
        // Save the language preference
        this.languageService.setLanguage(languageToApply);
        localStorage.setItem('preferredLanguage', languageToApply);
      }).catch((error) => {
        console.error('Error loading subscriber language:', error);
        // Use default on error
        this.languageService.setLanguage(languageToApply);
        localStorage.setItem('preferredLanguage', languageToApply);
      });
    } else {
      // No subscriber ID available, use default
      console.log('No subscriber ID found, using default language:', languageToApply);
      this.languageService.setLanguage(languageToApply);
      localStorage.setItem('preferredLanguage', languageToApply);
      return Promise.resolve();
    }
  }

  private routeUserBasedOnSetupStep(user: any) {
    const accountSetupStep = user.accountSetupStep || null;
    const userRole = user?.role?.identification;
    const isSA = userRole === 'SA';
    const isApproved = user?.status === 'APPROVED';
    
    // Get language from localStorage (set by loadLanguagePreference)
    const language = this.languageService.getCurrentLang();
    
    console.log('Routing user based on setup step:', {
      accountSetupStep,
      userRole,
      termsAccepted: user?.termsAccepted,
      status: user?.status,
      isApproved,
      language
    });
    
    // Helper function to get appropriate dashboard route based on user role
    // Note: Language routing is handled by baseHref, so we don't add language prefixes to routes
    const getDashboardRoute = () => {
      return this.getDashboardRouteByRole(userRole);
    };
    
    // Helper function to add language prefix to a route (deprecated - baseHref handles this)
    // Keeping for backward compatibility but routes should be language-agnostic
    const addLanguagePrefix = (route: string): string => {
      // Don't add language prefixes - baseHref handles language routing
      return route;
    };
    
    // For SA users, follow the 4-step setup process:
    // Step 1: Password reset/setup (accountSetupStep = 1)
    // Step 2: User account setup (accountSetupStep = 2)
    // Step 3: Terms and conditions (accountSetupStep = 3, check termsAccepted)
    // Step 4: Chargebee subscription (after setup complete + terms accepted)
    
    if (isSA) {
      // SA User Flow
      
      // If user is approved and has accepted terms, treat as setup complete regardless of accountSetupStep
      if (isApproved && user?.termsAccepted) {
        console.log('SA user is APPROVED and has accepted terms - checking subscription directly');
        // User has completed all setup - check subscription
        this.checkSubscriptionAndRoute(userRole, getDashboardRoute());
        return;
      }
      
      console.log('SA user setup incomplete or not approved yet - routing through setup steps');
      
      // Handle each setup step for non-approved or incomplete users
      switch (accountSetupStep) {
        case 1:
          // Step 1: Password Setup
          setTimeout(() => {
            this.router.navigate([addLanguagePrefix('/user-account-setup')], { queryParams: { step: '1' } });
          }, 0);
          break;
        case 2:
          // Step 2: Personal Information
          setTimeout(() => {
            this.router.navigate([addLanguagePrefix('/user-account-setup')], { queryParams: { step: '2' } });
          }, 0);
          break;
        case 3:
          // Step 3: Check terms acceptance
          if (!user?.termsAccepted) {
            // Show terms modal
            setTimeout(() => {
              this.router.navigate([addLanguagePrefix('/user-account-setup')]);
            }, 0);
          } else if (!isApproved) {
            // Terms accepted but not approved yet
            setTimeout(() => {
              this.router.navigate([addLanguagePrefix('/account-pending-approval')]);
            }, 0);
          } else {
            // This should be caught by the check above, but as fallback
            this.checkSubscriptionAndRoute(userRole, getDashboardRoute());
          }
          break;
        case null:
        default:
          // Setup complete - check subscription status if approved
          if (isApproved) {
            this.checkSubscriptionAndRoute(userRole, getDashboardRoute());
          } else {
            // Not approved yet, redirect to pending approval
            setTimeout(() => {
              this.router.navigate([addLanguagePrefix('/account-pending-approval')]);
            }, 0);
          }
          break;
      }
    } else {
      // Non-SA Users (A, FSR, CSM, SM, etc.)
      switch (accountSetupStep) {
        case 1:
          // Step 1: Password Setup
          setTimeout(() => {
            this.router.navigate([addLanguagePrefix('/user-account-setup')], { queryParams: { step: '1' } });
          }, 0);
          break;
        case 2:
          // Step 2: Personal Information
          setTimeout(() => {
            this.router.navigate([addLanguagePrefix('/user-account-setup')], { queryParams: { step: '2' } });
          }, 0);
          break;
        case 3:
        case null:
        default:
          // Setup complete - check subscription for A/FSR if approved
          if (isApproved && (userRole === 'A' || userRole === 'FSR')) {
            this.checkSubscriptionAndRoute(userRole, getDashboardRoute());
          } else if (isApproved) {
            // Other roles (CSM, SM, etc.) go straight to dashboard
            setTimeout(() => {
              this.router.navigate([getDashboardRoute()]);
            }, 0);
          } else {
            // Not approved yet
            setTimeout(() => {
              this.router.navigate([addLanguagePrefix('/account-pending-approval')]);
            }, 0);
          }
          break;
      }
    }
  }

  private checkSubscriptionAndRoute(userRole: string, dashboardRoute: string) {
    const language = this.languageService.getCurrentLang();
    
    console.log('Checking subscription status for', userRole);
    
    this.userService.checkSubscriptionStatus().subscribe({
      next: (subscriptionStatus) => {
        console.log('Subscription status:', subscriptionStatus);
        
        if (!subscriptionStatus.isValid) {
          if (subscriptionStatus.requiresPayment) {
            // SA user - redirect to subscription page
            setTimeout(() => {
              const route = this.languageService.isLanguageFeatureEnabled() && language && language !== 'en' 
                ? `/${language}/payment/sub` 
                : '/payment/sub';
              this.router.navigate([route]);
            }, 2000);
          } else {
            // A/FSR user - redirect to subscription inactive page
            const route = this.languageService.isLanguageFeatureEnabled() && language && language !== 'en' 
              ? `/${language}/subscription-inactive` 
              : '/subscription-inactive';
            this.router.navigate([route], {
              state: {
                message: subscriptionStatus.message,
                userRole: userRole
              }
            });
          }
        } else {
          // Subscription is active - proceed to dashboard
          setTimeout(() => {
            this.router.navigate([dashboardRoute]);
          }, 0);
        }
      },
      error: (error) => {
        console.error('Error checking subscription:', error);
        
        setTimeout(() => {
          this.router.navigate([dashboardRoute]);
        }, 1000);
      }
    });
  }

  /**
   * Get the appropriate dashboard route based on user role
   * @param roleIdentification - The role identification string
   * @returns The dashboard route path
   */
  private getDashboardRouteByRole(roleIdentification?: string): string {
    switch (roleIdentification) {
      case 'FSR':
        return '/admin/fsr-dashboard';
      case 'SA':
      case 'A':
        return '/admin/admin-dashboard';
      case 'CSM':
      case 'SM':
        return '/admin/user-management';
      default:
        // Default fallback for unknown roles or undefined
        return '/admin/user-management';
    }
  }
}
